import type { FilingProvider } from '../api/tax-filing';
import type { CostBasisMethod } from '../api/taxLots';

export type RecommendationTransactionsSource = 'rebalancing' | 'tax-optimisation';
export type RecommendationTransactionsView = 'transactions' | 'taxReports';
export type RecommendationTransactionsFocus =
  | 'transaction-list'
  | 'lot-evidence'
  | 'tax-report-slice';

export interface RecommendationTransactionsHandoffContext {
  handoff: 'recommendation-evidence';
  source: RecommendationTransactionsSource;
  view: RecommendationTransactionsView;
  focus: RecommendationTransactionsFocus;
  proofLabel: string;
  occurredAt?: string | null;
  asset?: string | null;
  year?: number;
  provider?: FilingProvider | null;
  method?: CostBasisMethod | null;
  reasonLabel?: string | null;
  reasonValue?: string | null;
  q?: string | null;
}

const VALID_SOURCES: RecommendationTransactionsSource[] = ['rebalancing', 'tax-optimisation'];
const VALID_VIEWS: RecommendationTransactionsView[] = ['transactions', 'taxReports'];
const VALID_FOCUSES: RecommendationTransactionsFocus[] = [
  'transaction-list',
  'lot-evidence',
  'tax-report-slice',
];
const VALID_PROVIDERS: FilingProvider[] = ['turbotax', 'hmrc', 'csv'];
const VALID_METHODS: CostBasisMethod[] = ['fifo', 'lifo', 'hifo'];

function normalizeString(value: string | null): string | null {
  const trimmed = value?.trim();
  return trimmed ? trimmed : null;
}

function setIfPresent(
  params: URLSearchParams,
  key: string,
  value: string | number | null | undefined,
) {
  if (value === null || value === undefined || value === '') {
    return;
  }

  params.set(key, String(value));
}

export function appendRecommendationTransactionsHandoffParams(
  params: URLSearchParams,
  context: RecommendationTransactionsHandoffContext | null,
) {
  if (!context) {
    return params;
  }

  params.set('handoff', context.handoff);
  params.set('source', context.source);
  if (!params.has('view')) {
    params.set('view', context.view);
  }
  params.set('focus', context.focus);
  params.set('proofLabel', context.proofLabel);
  setIfPresent(params, 'occurredAt', context.occurredAt);
  setIfPresent(params, 'asset', context.asset);
  setIfPresent(params, 'year', context.year);
  setIfPresent(params, 'provider', context.provider);
  setIfPresent(params, 'method', context.method);
  setIfPresent(params, 'reasonLabel', context.reasonLabel);
  setIfPresent(params, 'reasonValue', context.reasonValue);
  if (!params.has('q')) {
    setIfPresent(params, 'q', context.q);
  }

  return params;
}

export function getRecommendationTransactionsHandoffPath(
  context: RecommendationTransactionsHandoffContext,
): string {
  const params = appendRecommendationTransactionsHandoffParams(new URLSearchParams(), context);
  return `/transactions?${params.toString()}`;
}

export function readRecommendationTransactionsHandoff(
  searchParams: URLSearchParams,
): RecommendationTransactionsHandoffContext | null {
  if (searchParams.get('handoff') !== 'recommendation-evidence') {
    return null;
  }

  const source = normalizeString(searchParams.get('source'));
  const view = normalizeString(searchParams.get('view'));
  const focus = normalizeString(searchParams.get('focus'));
  const proofLabel = normalizeString(searchParams.get('proofLabel'));

  if (
    !source
    || !view
    || !focus
    || !proofLabel
    || !VALID_SOURCES.includes(source as RecommendationTransactionsSource)
    || !VALID_VIEWS.includes(view as RecommendationTransactionsView)
    || !VALID_FOCUSES.includes(focus as RecommendationTransactionsFocus)
  ) {
    return null;
  }

  const rawYear = normalizeString(searchParams.get('year'));
  const year = rawYear ? Number(rawYear) : undefined;
  const provider = normalizeString(searchParams.get('provider'));
  const method = normalizeString(searchParams.get('method'));

  if (provider && !VALID_PROVIDERS.includes(provider as FilingProvider)) {
    return null;
  }

  if (method && !VALID_METHODS.includes(method as CostBasisMethod)) {
    return null;
  }

  return {
    handoff: 'recommendation-evidence',
    source: source as RecommendationTransactionsSource,
    view: view as RecommendationTransactionsView,
    focus: focus as RecommendationTransactionsFocus,
    proofLabel,
    occurredAt: normalizeString(searchParams.get('occurredAt')),
    asset: normalizeString(searchParams.get('asset')),
    year: year !== undefined && Number.isFinite(year) ? year : undefined,
    provider: (provider as FilingProvider | null) ?? null,
    method: (method as CostBasisMethod | null) ?? null,
    reasonLabel: normalizeString(searchParams.get('reasonLabel')),
    reasonValue: normalizeString(searchParams.get('reasonValue')),
    q: normalizeString(searchParams.get('q')),
  };
}

export function getRecommendationTransactionsSourceLabel(
  source: RecommendationTransactionsSource,
): '/rebalancing' | '/tax-optimisation' {
  return source === 'rebalancing' ? '/rebalancing' : '/tax-optimisation';
}

export function getRecommendationTransactionsFocusLabel(
  focus: RecommendationTransactionsFocus,
): 'Transactions' | 'Lot evidence' | 'Tax reports' {
  if (focus === 'lot-evidence') return 'Lot evidence';
  if (focus === 'tax-report-slice') return 'Tax reports';
  return 'Transactions';
}
